// ============================================================
// Recipe Cost — backend API layer
// ============================================================
// Thin wrapper over AuthUtil (Bearer-token axios) that talks to the
// Grails RecipeCostController (/recipeCost/*). Every call is tenant-scoped
// by locationId, matching the BusinessTracker / Loans convention.
//
// All methods resolve to the server's `[success, ...]` map (response.data)
// and throw on transport/auth error (AuthUtil handles 401/403 → logout).

import AuthUtil from '../../redux/AuthUtil';
import { RECIPE_COST } from '../global/Server/endpoints';

const post = async (url, payload) => {
  const res = await AuthUtil.postRequest(url, payload || {});
  return res?.data;
};

const RecipeCostApi = {
  // ── Master List ──
  listIngredients: (locationId) =>
    post(RECIPE_COST.LIST_INGREDIENTS, { locationId }),

  saveIngredient: (locationId, ingredient) =>
    post(RECIPE_COST.SAVE_INGREDIENT, { locationId, ...ingredient }),

  deleteIngredient: (locationId, ingredientId) =>
    post(RECIPE_COST.DELETE_INGREDIENT, { locationId, ingredientId }),

  seedIngredients: (locationId, force = false) =>
    post(RECIPE_COST.SEED_INGREDIENTS, { locationId, force }),

  // ── Recipes persistence ──
  listRecipes: (locationId) =>
    post(RECIPE_COST.LIST_RECIPES, { locationId }),

  saveRecipes: (locationId, recipes) =>
    post(RECIPE_COST.SAVE_RECIPES, { locationId, recipes }),

  // ── Migration ──
  backupRecipes: (locationId, recipes) =>
    post(RECIPE_COST.BACKUP_RECIPES, { locationId, recipes }),

  applyMigration: (locationId, recipes) =>
    post(RECIPE_COST.APPLY_MIGRATION, { locationId, recipes }),

  revertMigration: (locationId) =>
    post(RECIPE_COST.REVERT_MIGRATION, { locationId }),

  // ── Analytics ──
  // summaries: [{ id, name, currentPrice, realCostPerUnit, profitPerUnit, actualMargin }]
  analytics: (locationId, recipes) =>
    post(RECIPE_COST.ANALYTICS, { locationId, recipes }),
};

// Server ingredient records use dry_goods; the mockup's Master List UI uses
// 'dry'. These two helpers translate the category enum across the boundary
// so seeded/persisted data renders in the right group.
export const serverToClientIngredient = (i) => ({
  ...i,
  id: i.ingredientId || i.id,
  category: i.category === 'dry_goods' ? 'dry' : i.category,
  purchasePrice: Number(i.purchasePrice) || 0,
  purchaseQty: Number(i.purchaseQty) || 0,
  mermaPercent: Number(i.mermaPercent) || 0,
});

export const clientToServerIngredient = (i) => ({
  ...i,
  ingredientId: i.id,
  category: i.category === 'dry' ? 'dry_goods' : i.category,
});

export default RecipeCostApi;
